import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  City,
  CreateOrUpdateUserResponse,
  CreditCard,
  CreateTaxRefundMemberResponse,
  CustomerCreditCardResponse,
  PersonalDetails,
  UpdatePolicyResponse,
  USER_INFO,
  ResponseError,
  GetTaxRefundInfoResponse,
  GeneralErrorResponse,
  CheckOrCreateOtpError,
  GetCCInfoError,
} from "src/utils/types";
import { API_RESULT } from "src/utils/enums";
import {
  checkOrCreateOtp,
  createOrUpdateUser,
  createTaxRefundMember,
  fetchAddressSuggestions,
  fetchCitySuggestions,
  fetchZipCode,
  getCCInfo,
  getCountries,
  getPersonalDetails,
  getTaxRefundInfo,
  updateCCInfo,
  updatePersonalDetails,
  updatePolicy,
} from "./UserApi";
import { parseResponseError } from "src/utils/utils";
interface userState {
  userInfo: USER_INFO;
  getCCInfoResStatus: API_RESULT;
  getCCInfoRes: CustomerCreditCardResponse;
  citiesResStatus: API_RESULT;
  cities: any;
  streetsResStatus: API_RESULT;
  streets: any;
  zipCodeResStatus: API_RESULT;
  zipCode: string;
  selectedStreet: any;
  selectedCity: City;
  creditCards: CreditCard[];
  personalDetailsResStatus: API_RESULT;
  personalDetailsRes: PersonalDetails;
  updateCCInfoResStatus: API_RESULT;
  updateCCInfoRes: any;
  checkOrCreateOtpResStatus: API_RESULT;
  checkOrCreateOtpErrorRes: CheckOrCreateOtpError;
  checkOrCreateOtpRes: any;
  updatePersonalDetailsResStatus: API_RESULT;
  updatePersonalDetailsRes: any;
  updatePolicyResStatus: API_RESULT;
  updatePolicyRes: UpdatePolicyResponse;
  taxRefundInfoResStatus: API_RESULT;
  taxRefundInfoRes: GetTaxRefundInfoResponse;
  createOrUpdateUserResStatus: API_RESULT;
  createOrUpdateUserRes: CreateOrUpdateUserResponse;
  createOrUpdateUserError: any;
  countriesResStatus: API_RESULT;
  countries: string[];
  createTaxRefundMemberResStatus: API_RESULT;
  createTaxRefundMemberRes: CreateTaxRefundMemberResponse;
  taxRefundInfoError: GeneralErrorResponse;
  getCCInfoError: GetCCInfoError;
  createTaxRefundMemberError: any;
}
const initialState: userState = {
  userInfo: {
    email: "",
    address: "",
    zip: "",
    city: "",
    apartment: "",
  },
  getCCInfoResStatus: API_RESULT.IDLE,
  getCCInfoRes: {} as CustomerCreditCardResponse,
  getCCInfoError: {} as GetCCInfoError,
  citiesResStatus: API_RESULT.IDLE,
  cities: [],
  streetsResStatus: API_RESULT.IDLE,
  streets: [],
  zipCodeResStatus: API_RESULT.IDLE,
  zipCode: "",
  selectedCity: { name: "", id: "" },
  selectedStreet: { name: "", id: "" },
  creditCards: [],
  personalDetailsResStatus: API_RESULT.IDLE,
  personalDetailsRes: {} as PersonalDetails,
  checkOrCreateOtpResStatus: API_RESULT.IDLE,
  checkOrCreateOtpErrorRes: {} as CheckOrCreateOtpError,
  checkOrCreateOtpRes: {},
  updatePersonalDetailsResStatus: API_RESULT.IDLE,
  updatePersonalDetailsRes: {} as PersonalDetails,
  updatePolicyResStatus: API_RESULT.IDLE,
  updatePolicyRes: {} as UpdatePolicyResponse,
  updateCCInfoResStatus: API_RESULT.IDLE,
  updateCCInfoRes: {} as CustomerCreditCardResponse,
  taxRefundInfoResStatus: API_RESULT.IDLE,
  taxRefundInfoRes: {} as GetTaxRefundInfoResponse,
  createOrUpdateUserResStatus: API_RESULT.IDLE,
  createOrUpdateUserRes: {} as CreateOrUpdateUserResponse,
  createOrUpdateUserError: {} as any,
  countriesResStatus: API_RESULT.IDLE,
  countries: [] as string[],
  createTaxRefundMemberResStatus: API_RESULT.IDLE,
  createTaxRefundMemberRes: {} as CreateTaxRefundMemberResponse,
  createTaxRefundMemberError: {} as any,
  taxRefundInfoError: {} as GeneralErrorResponse,
};

/* eslint-disable no-param-reassign */
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUserInfo(state, action: PayloadAction<any>) {
      state.userInfo = action.payload;
    },
    setCities: (state, action: PayloadAction<any>) => {
      state.cities = action.payload;
    },
    setSelectedCity: (state, action: PayloadAction<City>) => {
      state.selectedCity = action.payload;
    },
    setStreets: (state, action: PayloadAction<any>) => {
      state.streets = action.payload;
    },
    setSelectedStreet: (state, action: PayloadAction<City>) => {
      state.selectedStreet = action.payload;
    },
    setZipCode: (state, action: PayloadAction<string>) => {
      state.zipCode = action.payload;
    },
    setPersonalDetails: (state, action: PayloadAction<PersonalDetails>) => {
      state.personalDetailsRes = action.payload;
    },
    setCheckOrCreateOtpRes: (state, action: PayloadAction<any>) => {
      state.checkOrCreateOtpRes = action.payload;
    },
    setUpdatePersonalDetailsRes: (state, action: PayloadAction<any>) => {
      state.updatePersonalDetailsRes = action.payload;
    },
    setUpdatePolicyRes: (state, action: PayloadAction<any>) => {
      state.updatePolicyRes = action.payload;
    },
    setUpdateCCInfoRes: (state, action: PayloadAction<any>) => {
      state.updateCCInfoRes = action.payload;
    },
    resetCheckOrCreateOtpResStatus: (state) => {
      state.checkOrCreateOtpResStatus = API_RESULT.IDLE;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCCInfo.pending, (state) => {
        state.getCCInfoResStatus = API_RESULT.LOADING;
      })
      .addCase(getCCInfo.fulfilled, (state, action) => {
        state.getCCInfoResStatus = API_RESULT.SUCCEEDED;
        state.getCCInfoRes = action.payload;
        state.creditCards = action.payload.creditCards;
      })
      .addCase(getCCInfo.rejected, (state, action) => {
        state.getCCInfoResStatus = API_RESULT.FAILED;
        state.getCCInfoError = parseResponseError(
          action.error,
          {} as GetCCInfoError
        );
      })
      .addCase(fetchCitySuggestions.pending, (state) => {
        state.citiesResStatus = API_RESULT.LOADING;
      })
      .addCase(fetchCitySuggestions.fulfilled, (state, action) => {
        state.citiesResStatus = API_RESULT.SUCCEEDED;
        state.cities = action.payload.cities;
      })
      .addCase(fetchCitySuggestions.rejected, (state) => {
        state.citiesResStatus = API_RESULT.FAILED;
      })
      .addCase(fetchAddressSuggestions.pending, (state) => {
        state.streetsResStatus = API_RESULT.LOADING;
      })
      .addCase(fetchAddressSuggestions.fulfilled, (state, action) => {
        state.streetsResStatus = API_RESULT.SUCCEEDED;
        state.streets = action.payload.streets;
      })
      .addCase(fetchZipCode.rejected, (state) => {
        state.streetsResStatus = API_RESULT.FAILED;
      })
      .addCase(fetchZipCode.pending, (state) => {
        state.zipCodeResStatus = API_RESULT.LOADING;
      })
      .addCase(fetchZipCode.fulfilled, (state, action) => {
        state.zipCodeResStatus = API_RESULT.SUCCEEDED;
        state.zipCode = action.payload.zipcode;
      })
      .addCase(getPersonalDetails.pending, (state) => {
        state.personalDetailsResStatus = API_RESULT.LOADING;
      })
      .addCase(getPersonalDetails.fulfilled, (state, action) => {
        state.personalDetailsResStatus = API_RESULT.SUCCEEDED;
        state.personalDetailsRes = action.payload;
      })
      .addCase(checkOrCreateOtp.pending, (state) => {
        state.checkOrCreateOtpResStatus = API_RESULT.LOADING;
      })
      .addCase(checkOrCreateOtp.fulfilled, (state, action) => {
        state.checkOrCreateOtpResStatus = API_RESULT.SUCCEEDED;
        state.checkOrCreateOtpRes = action.payload;
      })
      .addCase(checkOrCreateOtp.rejected, (state, action) => {
        state.checkOrCreateOtpResStatus = API_RESULT.FAILED;
        state.checkOrCreateOtpErrorRes = parseResponseError(
          action.error,
          {} as CheckOrCreateOtpError
        );
      })
      .addCase(updatePersonalDetails.pending, (state) => {
        state.updatePersonalDetailsResStatus = API_RESULT.LOADING;
      })
      .addCase(updatePersonalDetails.fulfilled, (state, action) => {
        state.updatePersonalDetailsResStatus = API_RESULT.SUCCEEDED;
        state.updatePersonalDetailsRes = action.payload;
      })
      .addCase(updatePersonalDetails.rejected, (state) => {
        state.updatePersonalDetailsResStatus = API_RESULT.FAILED;
      })
      .addCase(updatePolicy.pending, (state) => {
        state.updatePolicyResStatus = API_RESULT.LOADING;
      })
      .addCase(updatePolicy.fulfilled, (state, action) => {
        state.updatePolicyResStatus = API_RESULT.SUCCEEDED;
        state.updatePolicyRes = action.payload;
      })
      .addCase(updatePolicy.rejected, (state) => {
        state.updatePolicyResStatus = API_RESULT.FAILED;
      })
      .addCase(updateCCInfo.pending, (state) => {
        state.updateCCInfoResStatus = API_RESULT.LOADING;
      })
      .addCase(updateCCInfo.fulfilled, (state, action) => {
        state.updateCCInfoResStatus = API_RESULT.SUCCEEDED;
        state.updateCCInfoRes = action.payload;
        state.creditCards = action.payload.creditCards;
      })
      .addCase(updateCCInfo.rejected, (state) => {
        state.updateCCInfoResStatus = API_RESULT.FAILED;
      })
      .addCase(getTaxRefundInfo.pending, (state) => {
        state.taxRefundInfoResStatus = API_RESULT.LOADING;
      })
      .addCase(getTaxRefundInfo.fulfilled, (state, action) => {
        state.taxRefundInfoResStatus = API_RESULT.SUCCEEDED;
        state.taxRefundInfoRes = action.payload;
      })
      .addCase(getTaxRefundInfo.rejected, (state, action) => {
        state.taxRefundInfoResStatus = API_RESULT.FAILED;
        state.taxRefundInfoError = parseResponseError(
          action.error,
          {} as GeneralErrorResponse
        );
      })
      .addCase(createOrUpdateUser.pending, (state) => {
        state.createOrUpdateUserResStatus = API_RESULT.LOADING;
      })
      .addCase(createOrUpdateUser.fulfilled, (state, action) => {
        state.createOrUpdateUserResStatus = API_RESULT.SUCCEEDED;
        state.createOrUpdateUserRes = action.payload;
      })
      .addCase(createOrUpdateUser.rejected, (state, action) => {
        state.createOrUpdateUserResStatus = API_RESULT.FAILED;
        state.createOrUpdateUserError = parseResponseError(
          action.error,
          {} as any
        );
      })
      .addCase(getCountries.pending, (state) => {
        state.countriesResStatus = API_RESULT.LOADING;
      })
      .addCase(getCountries.fulfilled, (state, action) => {
        state.countriesResStatus = API_RESULT.SUCCEEDED;
        state.countries = action.payload.countries;
      })
      .addCase(getCountries.rejected, (state) => {
        state.countriesResStatus = API_RESULT.FAILED;
      })
      .addCase(createTaxRefundMember.pending, (state) => {
        state.createTaxRefundMemberResStatus = API_RESULT.LOADING;
      })
      .addCase(createTaxRefundMember.fulfilled, (state, action) => {
        state.createTaxRefundMemberResStatus = API_RESULT.SUCCEEDED;
        state.createTaxRefundMemberRes = action.payload;
      })
      .addCase(createTaxRefundMember.rejected, (state, action) => {
        state.createTaxRefundMemberResStatus = API_RESULT.FAILED;
        state.createTaxRefundMemberError = parseResponseError(
          action.error,
          {} as any
        );
      });
  },
});

export const {
  setUserInfo,
  setCities,
  setSelectedCity,
  setStreets,
  setSelectedStreet,
  setZipCode,
  setPersonalDetails,
  setCheckOrCreateOtpRes,
  setUpdatePersonalDetailsRes,
  setUpdatePolicyRes,
  setUpdateCCInfoRes,
  resetCheckOrCreateOtpResStatus,
} = userSlice.actions;
export default userSlice.reducer;
